/** @format */

import { alpha } from '@mui/material/styles';

import { SERVICE_TYPE_INSTALLATION_SLUG } from './serviceTypeUi';

/** MUI palette keys for planning trip / intervention colors. */
export const PLANNING_SERVICE_TYPE_PALETTE_KEY = {
  installation: 'primary',
  vt: 'secondary',
  sav: 'error',
  diagnostic: 'purple',
  depannage: 'warning',
};

export const PLANNING_CLIENT_IN_A_HURRY_GOLD = '#E5B80B';
export const PLANNING_CLIENT_IN_A_HURRY_GOLD_DARK = '#7A5E10';
export const PLANNING_CLIENT_IN_A_HURRY_GRAY = '#9ca3af';
export const PLANNING_CLIENT_IN_EXTREME_HURRY_RED = '#DC2626';
export const PLANNING_CLIENT_IN_EXTREME_HURRY_RED_DARK = '#991B1B';

/** Red bolt — forwarding priority if a slot is released (planned trip). */
export function isInterventionClientInExtremeHurry(entity) {
  if (!entity) return false;
  return Boolean(entity.clientInExtremeHurry ?? entity.client_in_extreme_hurry);
}

/**
 * Any hurry flag (classic yellow or red forwarding priority).
 * Prefer extreme-specific helpers when color / copy must differ.
 */
export function isInterventionClientInAHurry(entity) {
  if (!entity) return false;
  if (isInterventionClientInExtremeHurry(entity)) return true;
  return Boolean(entity.clientInAHurry ?? entity.client_in_a_hurry);
}

/** Classic yellow « client pressé » only (not red forwarding priority). */
export function isInterventionClientInClassicHurry(entity) {
  if (!entity) return false;
  if (isInterventionClientInExtremeHurry(entity)) return false;
  return Boolean(entity.clientInAHurry ?? entity.client_in_a_hurry);
}

/** Sort rank: red forwarding (0) → classic hurry (1) → none (2). */
export function interventionClientHurrySortRank(entity) {
  if (isInterventionClientInExtremeHurry(entity)) return 0;
  if (isInterventionClientInClassicHurry(entity)) return 1;
  return 2;
}

/**
 * Dispo drawer sort rank. Planifiés pins red forwarding-priority first;
 * yellow is not a forwarding signal in that list.
 */
export function interventionDispoDrawerSortRank(entity, status) {
  if (status === 'planned') {
    return isInterventionClientInExtremeHurry(entity) ? 0 : 1;
  }
  return interventionClientHurrySortRank(entity);
}

export const CLIENT_HURRY_BOLT_LABEL = {
  classic: 'Client pressé',
  forwarding: 'À avancer en priorité',
};

/** Bolt tooltip: toggle copy when interactive, short labels otherwise. */
export function clientHurryBoltTooltip({
  extreme = false,
  classic = false,
  hasPlannedTrip = false,
  interactive = false,
} = {}) {
  if (!interactive) {
    return extreme
      ? CLIENT_HURRY_BOLT_LABEL.forwarding
      : CLIENT_HURRY_BOLT_LABEL.classic;
  }
  if (extreme) {
    return `${CLIENT_HURRY_BOLT_LABEL.forwarding}`;
  }
  if (classic) {
    return hasPlannedTrip
      ? `${CLIENT_HURRY_BOLT_LABEL.classic}`
      : `${CLIENT_HURRY_BOLT_LABEL.classic}`;
  }
  return hasPlannedTrip
    ? 'Marquer à avancer en priorité'
    : 'Marquer le client comme pressé';
}

/** Next hurry flags when the user clicks the bolt (mutually exclusive). */
export function nextClientHurryFlags(entity, { hasPlannedTrip = false } = {}) {
  const extreme = isInterventionClientInExtremeHurry(entity);
  const classic = isInterventionClientInClassicHurry(entity);

  if (extreme) {
    return { client_in_a_hurry: false, client_in_extreme_hurry: false };
  }
  if (classic) {
    if (hasPlannedTrip) {
      return { client_in_a_hurry: false, client_in_extreme_hurry: true };
    }
    return { client_in_a_hurry: false, client_in_extreme_hurry: false };
  }
  if (hasPlannedTrip) {
    return { client_in_a_hurry: false, client_in_extreme_hurry: true };
  }
  return { client_in_a_hurry: true, client_in_extreme_hurry: false };
}

export function resolvePlanningServiceTypeSlug(entity) {
  if (!entity) return SERVICE_TYPE_INSTALLATION_SLUG;
  if (typeof entity === 'string') return entity.toLowerCase();
  if (entity.serviceTypeSlug) {
    return String(entity.serviceTypeSlug).toLowerCase();
  }
  if (entity.service_type_slug) {
    return String(entity.service_type_slug).toLowerCase();
  }
  if (entity.slug) {
    const slug = String(entity.slug).toLowerCase();
    if (PLANNING_SERVICE_TYPE_PALETTE_KEY[slug]) {
      return slug;
    }
  }
  if (entity.visit_type === 'sav' || entity.visit_type === 'vt') {
    return entity.visit_type;
  }
  const subLabel = (entity.serviceTypeLabel || '').toLowerCase();
  if (subLabel.includes('sav')) return 'sav';
  if (subLabel.includes('visite') || subLabel === 'vt') return 'vt';
  if (subLabel.includes('diagnostic')) return 'diagnostic';
  if (subLabel.includes('dépannage') || subLabel.includes('depannage')) {
    return 'depannage';
  }
  if (subLabel.includes('installation') || subLabel.includes('chantier')) {
    return SERVICE_TYPE_INSTALLATION_SLUG;
  }
  const domainSlug = (entity.domain || '').toLowerCase();
  if (domainSlug && PLANNING_SERVICE_TYPE_PALETTE_KEY[domainSlug]) {
    return domainSlug;
  }

  return SERVICE_TYPE_INSTALLATION_SLUG;
}

/** Color source for a week-grid block (plan + trip + optional layer). */
export function resolveTripPlanningColorSource({
  plan,
  trip,
  interventionLayer,
  isSav,
  isVt,
}) {
  if (isSav || plan?.afterSalesVisitId) return 'sav';
  if (isVt || plan?.technicalVisitId) return 'vt';
  if (interventionLayer) return interventionLayer;
  if (trip) return trip;
  return SERVICE_TYPE_INSTALLATION_SLUG;
}

function paletteEntry(theme, slug) {
  const paletteKey =
    PLANNING_SERVICE_TYPE_PALETTE_KEY[slug] ||
    PLANNING_SERVICE_TYPE_PALETTE_KEY.installation;
  return theme.palette[paletteKey] || theme.palette.primary;
}

/** Gold block colors for urgent / “client pressé” interventions. */
export function planningClientInAHurryColors() {
  const gold = PLANNING_CLIENT_IN_A_HURRY_GOLD;
  return {
    bg: alpha(gold, 0.4),
    t: PLANNING_CLIENT_IN_A_HURRY_GOLD_DARK,
    av: gold,
    at: '#fff',
  };
}

/** Red block colors for forwarding-priority interventions. */
export function planningClientInExtremeHurryColors() {
  const red = PLANNING_CLIENT_IN_EXTREME_HURRY_RED;
  return {
    bg: alpha(red, 0.35),
    t: PLANNING_CLIENT_IN_EXTREME_HURRY_RED_DARK,
    av: red,
    at: '#fff',
  };
}

function paletteBlockColors(palette, fallbackMain = '#1976d2') {
  const main = palette?.main || fallbackMain;
  const dark =
    palette?.dark && String(palette.dark).length > 0 ? palette.dark : main;
  const contrastText =
    palette?.contrastText && String(palette.contrastText).length > 0
      ? palette.contrastText
      : '#fff';
  return {
    bg: alpha(main, 0.4),
    t: dark,
    av: main,
    at: contrastText,
  };
}

/** Same shape as `COLORS` entries in constants.js (bg, t, av, at). */
export function planningInterventionColors(theme, entityOrSlug) {
  if (isInterventionClientInExtremeHurry(entityOrSlug)) {
    return planningClientInExtremeHurryColors();
  }
  if (isInterventionClientInClassicHurry(entityOrSlug)) {
    return planningClientInAHurryColors();
  }
  const slug = resolvePlanningServiceTypeSlug(entityOrSlug);
  return paletteBlockColors(
    paletteEntry(theme, slug),
    theme.palette.primary.main,
  );
}

/**
 * Sidebar filter / quick-add card colors.
 * Yellow (warning) is the finalisation chip, not « client pressé ».
 */
export function planningFilterColors(
  theme,
  entityOrSlug,
  { finalisation = false } = {},
) {
  const palette = finalisation
    ? theme.palette.warning
    : paletteEntry(theme, resolvePlanningServiceTypeSlug(entityOrSlug));
  return paletteBlockColors(palette, theme.palette.primary.main);
}

/** Opaque block background for expanded overlap hover (default trip bg is 40% alpha). */
export function planningInterventionOpaqueBg(theme, entityOrSlug) {
  if (isInterventionClientInExtremeHurry(entityOrSlug)) {
    return '#FECACA';
  }
  if (isInterventionClientInClassicHurry(entityOrSlug)) {
    return '#F9EEC8';
  }
  const slug = resolvePlanningServiceTypeSlug(entityOrSlug);
  const palette = paletteEntry(theme, slug);
  if (palette.light) return palette.light;
  return palette.main;
}

export function planningPinColors(theme, entityOrSlug) {
  if (isInterventionClientInExtremeHurry(entityOrSlug)) {
    return {
      fillColor: PLANNING_CLIENT_IN_EXTREME_HURRY_RED,
      strokeColor: PLANNING_CLIENT_IN_EXTREME_HURRY_RED_DARK,
    };
  }
  if (isInterventionClientInClassicHurry(entityOrSlug)) {
    return {
      fillColor: PLANNING_CLIENT_IN_A_HURRY_GOLD,
      strokeColor: PLANNING_CLIENT_IN_A_HURRY_GOLD_DARK,
    };
  }
  const { av, t } = planningInterventionColors(theme, entityOrSlug);
  return { fillColor: av, strokeColor: t };
}

/** Map pins use the same palette as `planningFilterColors`. */
export function planningFilterPinColors(theme, entityOrSlug, options = {}) {
  const { av, t } = planningFilterColors(theme, entityOrSlug, options);
  return { fillColor: av, strokeColor: t };
}

export function findInterventionLayerForTrip(trip, interventionLayers) {
  if (!trip || !interventionLayers?.length) return null;
  const layerId = trip.interventionLayerId ?? trip.interventionId ?? null;
  if (layerId == null) return null;
  return (
    interventionLayers.find((row) => Number(row.id) === Number(layerId)) ?? null
  );
}

export function findInterventionLayerForPlan(
  plan,
  snapshot,
  interventionLayers,
) {
  if (!plan) return null;
  const layers = interventionLayers || [];
  if (plan.interventionLayerId != null) {
    return (
      layers.find(
        (row) => Number(row.id) === Number(plan.interventionLayerId),
      ) ?? null
    );
  }
  const tripId = plan.tripId ?? plan.interventionId;
  const trip = snapshot?.interventions?.find(
    (row) => Number(row.id) === Number(tripId),
  );
  const layerFromList = findInterventionLayerForTrip(trip, layers);
  if (layerFromList) return layerFromList;
  if (trip?.serviceTypeSlug) return trip;
  return null;
}
